<template>
  <listing-layout>
    <h1 slot="heading" class="h2 mb-3">Judge Survey</h1>
    <div slot="content">
      <div class="alert alert-warning" v-if="error">{{ error }}</div>
      <div class="alert alert-success" v-else-if="success">{{ success }}</div>
      <div v-if="loaded">
        <div v-if="!closed">
          <form @submit.prevent="sendSurveyResults">
            <div class="responding-judge mb-3">
              <select
                :class="['form-control']"
                name="responding_judge_id"
                id="select_responding_judge"
                v-model="respondingJudgeId"
                :required="!respondingJudgeId"
              >
                <option v-for="judge in judges" :value="judge.id">{{ judge.user.first_name }} {{ judge.user.last_name }}</option>
              </select>
            </div>
            <div v-if="respondingJudgeId">
              <div v-if="surveyLoaded">
                <div class="surveys" v-if="surveys.length">
                  <div class="draft" v-for="(draft, draftKey) in surveys">
                    <div>
                      <h2 slot="heading" class="h3 mb-3">{{ draft.draft.name }}</h2>
                      <div class="mb-2" v-for="(survey) in draft.events">
                        <div v-for="(judge) in survey.event.judges">
                          <div class="event" v-if="judge.id !== respondingJudgeId && showJudge(survey, respondingJudgeId, judge.id, draftKey)">
                            <h3 slot="heading" class="h4 mb-2 font-weight-bold">{{ survey.event.name }}, {{ judge.user.first_name }} {{ judge.user.last_name }}</h3>
                            <div class="questions" v-for="(question, questionKey) in surveyQuestions">
                              <div class="number">
                                <span>{{ Number(questionKey) + 1 }}</span>
                              </div>
                              <div class="question">
                                <div class="question-text">{{ getQuestionText(survey, judge.id, question) }}</div>
                                <div class="answers d-flex">
                                  <div class="answer" v-for="answer in question.answers">
                                    <div class="answer-info">
                                      <input
                                        type="radio"
                                        :class="['input-answer custom-control-input']"
                                        :id="`answer[${survey.event.id}][${judge.id}][${question.id}][${answer.id}]`"
                                        :name="`answer[${survey.event.id}][${judge.id}][${question.id}]`"
                                        :value="answer.id"
                                        v-model="userAnswers[survey.id].events[survey.event_id].judges[judge.id].questions[question.id]"
                                        @change="updateUserAnswers(survey, judge.id, question.id)"
                                        required
                                      >
                                      <label :for="`answer[${survey.event.id}][${judge.id}][${question.id}][${answer.id}]`">
                                        {{ answer.answer }}
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                <div class="additional-questions">
                                  <div class="additional-question" v-for="additionalQuestion in getAdditionalQuestions(question)">
                                    <div v-if="showAdditionalQuestions(survey, judge.id, additionalQuestion)">
                                      <label :for="`answer[${survey.event.id}][${judge.id}][${additionalQuestion.id}]`" class="question-text">
                                        {{ getQuestionText(survey, judge.id, additionalQuestion) }}
                                      </label>
                                      <textarea
                                        :class="['form-control']"
                                        :id="`answer[${survey.event.id}][${judge.id}][${additionalQuestion.id}]`"
                                        :name="`answer[${survey.event.id}][${judge.id}][${additionalQuestion.id}]`"
                                        v-model="userAnswers[survey.id].events[survey.event_id].judges[judge.id].questions[additionalQuestion.id]"
                                        placeholder="Enter an answer"
                                        @change="updateUserAnswers(survey, judge.id, additionalQuestion.id, 1)"
                                        :required="isQuestionRequired(survey, judge.id, additionalQuestion)"
                                      ></textarea>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    class="btn btn-primary"
                  >Submit Survey</button>
                </div>
                <div v-else>
                  There are no surveys for this judge.
                </div>
              </div>
              <div class="loader" v-else>
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div v-else>
          Sorry, the survey has already been closed.
        </div>
      </div>
      <div class="loader" v-else>
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  </listing-layout>
</template>

<script>
import listingLayout from '@/components/ListingLayout'
import axios from "axios"

export default {
  name: "Survey",
  components: {
    'listing-layout': listingLayout
  },
  data() {
    return {
      error: '',
      success: '',
      surveys: [],
      questions: [],
      judges: [],
      respondingJudgeId: "",
      userAnswers: [],
      resultUserAnswers: [],
      loaded: false,
      surveyLoaded: false,
      closed: false
    }
  },
  computed: {
    surveyQuestions() {
      return this.questions.filter(question => !question.parent_question_id)
    }
  },
  watch: {
    resultUserAnswers() {
      this.userAnswers = [...this.userAnswers]
    },
    respondingJudgeId() {
      this.success = ''
      if (this.loaded) {
        this.getSurveys()
      }
    }
  },
  mounted() {
    this.getSurveys()
  },
  methods: {
    getSurveys() {
      this.surveyLoaded = false
      let configs = {}
      if (this.respondingJudgeId) {
        configs = {
          params: {
            responding_judge_id: this.respondingJudgeId
          }
        }
      }
      axios.get(`${this.$api}/judges/surveys`, configs).then((response) => {
        if (response.data.status === 'success') {
          this.error = ''
          const data = response.data.data

          this.judges = data.judges

          if (!this.loaded && this.judges && this.judges[0]) {
            this.respondingJudgeId = this.judges[0].id
          }
          this.surveys = [...data.surveys]
          this.questions = data.questions
          this.resultUserAnswers = []

          if (this.surveys) {
            const surveyData = this.surveys
            surveyData.forEach((item) => {
              item.events.forEach((survey) => {
                const surveyId = survey.id
                if (!this.userAnswers[surveyId]) {
                  this.userAnswers[surveyId] = {
                    ...this.userAnswers[surveyId],
                    events: []
                  }
                }
                // eslint-disable-next-line
                if (!this.userAnswers[surveyId].events[survey.event_id]) {
                  this.userAnswers[surveyId].events[survey.event_id] = {
                    ...this.userAnswers[surveyId].events[survey.event_id],
                    judges: []
                  }
                }

                survey.event.judges.forEach((judge) => {
                  this.userAnswers[surveyId].events[survey.event_id].judges[judge.id] = {
                    ...this.userAnswers[surveyId].events[survey.event_id].judges[judge.id],
                    questions: []
                  }
                })
              })
            })
          }
          this.loaded = true
          this.surveyLoaded = true
        } else if (response.data.status === 'survey_closed') {
          this.loaded = true
          this.closed = true
        } else {
          this.error = 'Unable to receive data from the server. Please reload the page.'
        }
      })
    },
    updateUserAnswers(survey, judgeId, questionId, custom = 0) {
      const answer = this.userAnswers[survey.id].events[survey.event_id].judges[judgeId].questions[questionId]
      const saveData = {
        survey_id: survey.id,
        judge_id: judgeId,
        question_id: questionId,
        answer,
        custom
      }

      // eslint-disable-next-line
      const index = this.resultUserAnswers.findIndex((item) => {
        return item.survey_id === survey.id && item.judge_id === judgeId && item.question_id === questionId
      })
      if (index === -1) {
        this.resultUserAnswers.push(saveData)
      } else {
        this.resultUserAnswers.splice(index, 1, saveData)
      }
    },
    showAdditionalQuestions(survey, judgeId, question) {
      const parentQuestion = this.questions.find(questionData => questionData?.id === question?.parent_question_id)

      if (!parentQuestion) {
        return false
      }

      const answerId = this.userAnswers[survey.id].events[survey.event_id].judges[judgeId].questions[parentQuestion.id]

      if (!answerId) {
        return false
      }

      const answers = parentQuestion.answers
      const answersWithAdditionalQuestions = answers.filter(answer => question.id === answer.additional_question_id)
      const answersWithAdditionalQuestionsIds = []
      if (answersWithAdditionalQuestions.length) {
        answersWithAdditionalQuestions.forEach((answer) => {
          answersWithAdditionalQuestionsIds.push(answer.id)
        })
      }
      return answersWithAdditionalQuestionsIds.includes(answerId)
    },
    getAdditionalQuestions(question) {
      const answers = question.answers
      const additionalQuestions = []
      const additionalQuestionsIds = []
      if (answers) {
        answers.forEach((answer) => {
          if (answer.additional_question && !additionalQuestionsIds.includes(answer.additional_question.id)) {
            additionalQuestionsIds.push(answer.additional_question.id)
            additionalQuestions.push(answer.additional_question)
          }
        })
      }
      return additionalQuestions
    },
    sendSurveyResults() {
      const resultUserAnswers = this.resultUserAnswers

      if (!this.respondingJudgeId) {
        this.error = 'Select the responding judge, please.'
        return
      }

      resultUserAnswers.map((item) => {
        item.responding_judge_id = this.respondingJudgeId
        return item
      })

      const data = resultUserAnswers.filter(item => item.responding_judge_id !== item.judge_id)

      axios.post(`${this.$api}/judges/surveys/send`, data).then((response) => {
        if (response.data.status === 'success') {
          this.error = ''
          this.success = 'Survey results successfully saved.'
          this.getSurveys()
        } else {
          this.success = ''
          this.error = 'Something went wrong. Please, try again later.'
        }
      })
    },
    showJudge(survey, respondingJudgeId, judgeId) {
      if (!respondingJudgeId) return true

      const respondingJudge = this.judges.find(judge => judge.id === respondingJudgeId)
      if (!respondingJudge) return false

      const surveyResponsible = respondingJudge.responding_survey_responsible
      if (!surveyResponsible) return true

      // eslint-disable-next-line
      const existingSurveyResponsible = surveyResponsible.find((item) => {
        return item.survey_id === survey.id && item.responding_judge_id === respondingJudgeId && item.judge_id === judgeId
      })
      return !existingSurveyResponsible
    },
    isQuestionRequired(survey, judgeId, question) {
      if (question?.slug === 'comments' && this.isPositivePerformanceRating(survey, judgeId)) {
        return false
      }
      return true
    },
    getQuestionText(survey, judgeId, question) {
      let questionText = question.question

      if (question?.slug === 'comments' && this.isPositivePerformanceRating(survey, judgeId)) {
        questionText = 'Comment:'
      }

      return questionText
    },
    isPositivePerformanceRating(survey, judgeId) {
      const performanceRatingQuestion = this.questions.find(item => item.slug === 'performance-rating')
      const answerId = this.userAnswers[survey.id].events[survey.event_id].judges[judgeId].questions[performanceRatingQuestion.id]
      const answer = performanceRatingQuestion.answers.find(item => item.id === answerId)

      return answer?.answer && ['Good', 'Excellent'].includes(answer.answer)
    }
  }
}
</script>

<style scoped>
.surveys .draft {
  margin-bottom: 20px;
}

.surveys .event {
  margin-bottom: 3rem;
}

.responding-judge select {
  max-width: 362px;
}

.questions {
  display: flex;
  max-width: 1200px;
}

.questions .question {
  flex-grow: 1;
}

.questions .number {
  display: flex;
  align-self: flex-start;
  margin-right: 20px;
}

.questions .number span {
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  font-size: 18px;
  color: var(--white);
  background-color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 50%;
  width: 25px;
  height: 25px;
}

.questions .question-text,
.additional-questions .question-text {
  margin-bottom: 3px;
  font-weight: bold;
  font-size: 18px;
}

.questions textarea,
.additional-questions textarea {
  min-height: 120px;
  max-height: 250px;
  width: 100%;
}

.answers .answer {
  margin-right: 10px;
}

.answers .answer:last-of-type {
  margin-right: 0;
}

.answer-info {
  display: -ms-flexbox;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
  position: relative;
}

.answer-info label {
  position: relative;
  padding-left: 32px;
  margin-right: 20px;
  text-align: start;
  font-weight: 400 !important;
}

.answer-info label::before {
  border-radius: 50%;
  border: 1px solid var(--light);
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  pointer-events: none;
  background-color: var(--gray-100);
}

.answer-info label::after {
  background: 50%/50% 50% no-repeat;
}

.answer-info label::after,
.answer-info label::before {
  position: absolute;
  top: 0.125rem;
  left: -0.05rem;
  display: block;
  width: 1.25rem;
  height: 1.25rem;
  content: '';
}

.answer-info input:checked + label::after {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='-4 -4 8 8'%3E%3Ccircle r='3' fill='%23fff'/%3E%3C/svg%3E");
}

.answer-info input:checked + label::before {
  color: var(--white);
  border-color: var(--primary);
  background-color: var(--primary);
}

.answer-info .custom-control-input {
  position: absolute;
  left: 0;
  z-index: -1;
  width: 1.25rem;
  opacity: 0;
}

@media (max-width: 767px) {

  .answers {
    flex-direction: column;
  }

  .questions textarea,
  .additional-questions textarea {
    min-height: 120px;
    max-height: 350px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Judges/Surveys/Survey.vue